import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Switch,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const AccessibilitySettingsScreen = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future navigation
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();

  // State for accessibility settings
  const [colorBlindMode, setColorBlindMode] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [screenReader, setScreenReader] = useState(false);

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
        }}
      >
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Vision</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingContent}>
              <Text style={styles.settingText}>Colour Blind Mode</Text>
              <Text style={styles.settingDescription}>
                Adjust colours to be more distinguishable for colour blind users
              </Text>
            </View>
            <Switch
              value={colorBlindMode}
              onValueChange={setColorBlindMode}
              trackColor={{
                false: palette.lightGray,
                true: palette.brightViolet,
              }}
              thumbColor={
                Platform.OS === 'ios' ? palette.white : palette.softGray
              }
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingContent}>
              <Text style={styles.settingText}>High Contrast</Text>
              <Text style={styles.settingDescription}>
                Increase contrast between elements for better visibility
              </Text>
            </View>
            <Switch
              value={highContrast}
              onValueChange={setHighContrast}
              trackColor={{
                false: palette.lightGray,
                true: palette.brightViolet,
              }}
              thumbColor={
                Platform.OS === 'ios' ? palette.white : palette.softGray
              }
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingContent}>
              <Text style={styles.settingText}>Large Text</Text>
              <Text style={styles.settingDescription}>
                Increase text size throughout the app
              </Text>
            </View>
            <Switch
              value={largeText}
              onValueChange={setLargeText}
              trackColor={{
                false: palette.lightGray,
                true: palette.brightViolet,
              }}
              thumbColor={
                Platform.OS === 'ios' ? palette.white : palette.softGray
              }
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Motion</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingContent}>
              <Text style={styles.settingText}>Reduce Motion</Text>
              <Text style={styles.settingDescription}>
                Minimize animations throughout the app
              </Text>
            </View>
            <Switch
              value={reduceMotion}
              onValueChange={setReduceMotion}
              trackColor={{
                false: palette.lightGray,
                true: palette.brightViolet,
              }}
              thumbColor={
                Platform.OS === 'ios' ? palette.white : palette.softGray
              }
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Screen Reader</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingContent}>
              <Text style={styles.settingText}>VoiceOver Descriptions</Text>
              <Text style={styles.settingDescription}>
                Enable detailed descriptions for screen readers
              </Text>
            </View>
            <Switch
              value={screenReader}
              onValueChange={setScreenReader}
              trackColor={{
                false: palette.lightGray,
                true: palette.brightViolet,
              }}
              thumbColor={
                Platform.OS === 'ios' ? palette.white : palette.softGray
              }
            />
          </View>
        </View>

        <View style={styles.infoSection}>
          <Ionicons
            name="information-circle-outline"
            size={24}
            color="#6B7280"
          />
          <Text style={styles.infoText}>
            These settings help make the app more accessible for everyone.
            Changes will be applied immediately.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: palette.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 60,
    paddingBottom: 16,
    backgroundColor: palette.white,
    borderBottomWidth: 1,
    borderBottomColor: palette.veryLightGray,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: palette.veryDarkBlue,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 20,
    paddingBottom: 0,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: palette.mediumGray2,
    marginBottom: 16,
    marginLeft: 4,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: palette.white,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: palette.veryLightGray,
  },
  settingContent: {
    flex: 1,
    marginRight: 16,
  },
  settingText: {
    fontSize: 16,
    color: palette.veryDarkBlue,
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 14,
    color: palette.darkGrayBlue,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 20,
    backgroundColor: palette.softGray,
    marginHorizontal: 20,
    marginTop: 20,
    borderRadius: 12,
  },
  infoText: {
    fontSize: 14,
    color: palette.darkGrayBlue,
    marginLeft: 12,
    flex: 1,
  },
});

export default AccessibilitySettingsScreen;
